import React from "react";
import { Phone, Mail, MessageCircle, MapPin, User } from "lucide-react";
import { CONTACT, whatsappLink } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageBits";
import LeadForm from "@/components/LeadForm";
import { Button } from "@/components/ui/button";

export default function Contact() {
  useSEO({ title: "Contact Us", description: "Contact Leaf Alchemy for premium tea powder and bulk supply. Call +91 99122 26145 or email hello@leafalchemy.shop." });

  const actions = [
    { icon: Phone, label: "Call Now", href: `tel:${CONTACT.phoneRaw}`, cls: "bg-forest text-cream", testid: "contact-call" },
    { icon: MessageCircle, label: "WhatsApp", href: whatsappLink(), cls: "bg-[#25D366] text-white", testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", href: `mailto:${CONTACT.email}`, cls: "bg-gold text-ink", testid: "contact-email" },
    { icon: MapPin, label: "Google Maps", href: CONTACT.location.mapsUrl, cls: "bg-cream border border-forest/20 text-forest", testid: "contact-maps" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Get in Touch" title="Let's Talk Tea"
        subtitle="Reach out for wholesale enquiries, samples or partnerships. We&rsquo;d love to hear from you." />

      <section className="py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <div className="bg-forest text-cream rounded-2xl p-8" data-testid="contact-info-card">
              <h2 className="font-display text-3xl text-gold">Leaf Alchemy</h2>
              <p className="text-cream/70 mt-1">Crafted by Nature, Perfected for You</p>
              <div className="mt-8 space-y-5">
                <div className="flex items-center gap-3"><User className="text-gold" size={18} /><div><p className="text-xs text-cream/60 uppercase tracking-wider">Point of Contact</p><p className="font-medium">{CONTACT.poc}</p></div></div>
                <div className="flex items-center gap-3"><Phone className="text-gold" size={18} /><div><p className="text-xs text-cream/60 uppercase tracking-wider">Phone</p><a href={`tel:${CONTACT.phoneRaw}`} className="font-medium hover:text-gold">{CONTACT.phone}</a></div></div>
                <div className="flex items-center gap-3"><Mail className="text-gold" size={18} /><div><p className="text-xs text-cream/60 uppercase tracking-wider">Email</p><a href={`mailto:${CONTACT.email}`} className="font-medium hover:text-gold break-all">{CONTACT.email}</a></div></div>
                <div className="flex items-start gap-3"><MapPin className="text-gold mt-1" size={18} /><div><p className="text-xs text-cream/60 uppercase tracking-wider">Business Location</p><a href={CONTACT.location.mapsUrl} target="_blank" rel="noreferrer" className="font-medium hover:text-gold break-words">{CONTACT.location.address}</a><p className="mt-1 text-sm text-cream/75">{CONTACT.location.displayAddress.replace(" • ", " • ")}</p></div></div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-8">
                {actions.map((a) => (
                  <a key={a.label} href={a.href} target="_blank" rel="noreferrer" data-testid={a.testid}>
                    <Button className={`w-full rounded-full ${a.cls} hover:opacity-90 transition-opacity`}>
                      <a.icon size={16} className="mr-2" /> {a.label}
                    </Button>
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-cream border border-gold/30 rounded-2xl p-7 shadow-[0_16px_48px_rgba(15,81,50,0.08)]" data-testid="contact-form-card">
              <h3 className="font-display text-2xl text-forest mb-1">Location</h3>
              <p className="text-sm text-muted-foreground mb-5">{CONTACT.location.displayAddress}</p>
              <div className="overflow-hidden rounded-2xl border border-gold/30 bg-white">
                <iframe
                  title="Leaf Alchemy location"
                  src={CONTACT.location.embedUrl}
                  loading="lazy"
                  className="w-full h-[260px] border-0"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                <a href={CONTACT.location.mapsUrl} target="_blank" rel="noreferrer" className="font-medium text-forest underline underline-offset-2 hover:text-gold">
                  Open in Google Maps
                </a>
              </div>
              <div className="mt-6">
                <h3 className="font-display text-2xl text-forest mb-1">Send a Message</h3>
                <p className="text-sm text-muted-foreground mb-5">We'll get back to you shortly.</p>
                <LeadForm type="contact" fields={["name", "phone", "email", "business_type", "message"]} submitLabel="Send Message" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
